// src/components/ServiceNow/BulkActionsBar.jsx
import React, { useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
} from "@mui/icons-material";
import { searchUsers } from "services/userServices";

const BulkActionsBar = ({
  totalTasks,
  selectedTasks,
  onSelectAll,
  onBulkUpdate,
  onRefresh,
  onToggleAll,
  taskStates,
  loading,
  allExpanded,
}) => {
  const [bulkAction, setBulkAction] = useState("");
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [bulkComment, setBulkComment] = useState("");
  const [assignToSearch, setAssignToSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [isWorkNote, setIsWorkNote] = useState(false);

  const allSelected = selectedTasks.length === totalTasks && totalTasks > 0;
  const someSelected =
    selectedTasks.length > 0 && selectedTasks.length < totalTasks;

  const handleSelectAllChange = (event) => {
    onSelectAll(event.target.checked);
  };

  const handleBulkStatusChange = (event) => {
    const selectedState = event.target.value;
    if (selectedState) {
      onBulkUpdate({ state: selectedState });
      setBulkAction("");
    }
  };

  const handleBulkCommentOpen = () => {
    setCommentDialogOpen(true);
  };

  const handleBulkReassignOpen = () => {
    setReassignDialogOpen(true);
  };

  const handleUserSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setUserOptions([]);
      return;
    }

    try {
      setUserSearchLoading(true);
      const users = await searchUsers(searchQuery);

      if (users && Array.isArray(users)) {
        setUserOptions(users);
      } else {
        setUserOptions([]);
      }
    } catch (err) {
      console.error("Error searching users:", err);
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleBulkCommentSubmit = () => {
    if (bulkComment.trim()) {
      const updateData = isWorkNote
        ? { work_notes: bulkComment }
        : { comments: bulkComment };

      onBulkUpdate(updateData);
      setBulkComment("");
      setIsWorkNote(false);
      setCommentDialogOpen(false);
    }
  };

  const handleBulkReassignSubmit = () => {
    if (selectedUser && selectedUser.sys_id) {
      const updateData = {
        assigned_to: selectedUser.sys_id,
        comments: `Tasks reassigned via ASTT to ${
          selectedUser.name || selectedUser.user_name
        }`,
      };

      onBulkUpdate(updateData);
      setSelectedUser(null);
      setAssignToSearch("");
      setUserOptions([]);
      setReassignDialogOpen(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#E3F2FD",
          border: "1px solid #90CAF9",
          borderRadius: 1,
          p: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left Side - Selection */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAllChange}
                sx={{
                  color: "#3299FF",
                  "&.Mui-checked": { color: "#3299FF" },
                  "&.MuiCheckbox-indeterminate": { color: "#3299FF" },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "#555",
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                Select All ({totalTasks} tasks)
              </Typography>
            }
          />

          {selectedTasks.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              }}
            >
              {selectedTasks.length} task{selectedTasks.length !== 1 ? "s" : ""}{" "}
              selected
            </Typography>
          )}
        </Box>

        {/* Right Side - Actions */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* Expand/Collapse All */}
          <Button
            variant="outlined"
            size="small"
            onClick={onToggleAll}
            startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            sx={{
              borderColor: "#ccc",
              color: "#555",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              "&:hover": {
                borderColor: "#3299FF",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            {allExpanded ? "Collapse All" : "Expand All"}
          </Button>

          {/* Bulk Status Change */}
          <Select
            value={bulkAction}
            onChange={handleBulkStatusChange}
            displayEmpty
            size="small"
            disabled={selectedTasks.length === 0 || loading}
            sx={{
              minWidth: 180,
              backgroundColor: "white",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ccc",
              },
            }}
          >
            <MenuItem value="" disabled>
              Bulk Status Change
            </MenuItem>
            {taskStates.map((state) => (
              <MenuItem
                key={state.value}
                value={state.value}
                sx={{ fontFamily: "Urbanist, Verdana, Arial, sans-serif" }}
              >
                → {state.label}
              </MenuItem>
            ))}
          </Select>

          {/* Bulk Comment */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleBulkCommentOpen}
            disabled={selectedTasks.length === 0 || loading}
            sx={{
              borderColor: "#ccc",
              color: "#555",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              "&:hover": {
                borderColor: "#3299FF",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Bulk Comment
          </Button>

          {/* Bulk Reassign */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleBulkReassignOpen}
            disabled={selectedTasks.length === 0 || loading}
            sx={{
              borderColor: "#ccc",
              color: "#555",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              "&:hover": {
                borderColor: "#3299FF",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Bulk Reassign
          </Button>

          {/* Refresh Button */}
          <Button
            variant="outlined"
            size="small"
            onClick={onRefresh}
            disabled={loading}
            startIcon={
              <RefreshIcon
                sx={{
                  animation: loading ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            }
            sx={{
              borderColor: "#ccc",
              color: "#555",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              "&:hover": {
                borderColor: "#3299FF",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Bulk Comment Dialog */}
      <Dialog
        open={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ fontFamily: "Urbanist, Verdana, Arial, sans-serif" }}
        >
          Add Bulk Comment
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: "#666",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            }}
          >
            This comment will be added to {selectedTasks.length} selected task
            {selectedTasks.length !== 1 ? "s" : ""}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            value={bulkComment}
            onChange={(e) => setBulkComment(e.target.value)}
            placeholder="Type your comment..."
            variant="outlined"
            sx={{
              mb: 2,
              "& .MuiInputBase-input": {
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isWorkNote}
                onChange={(e) => setIsWorkNote(e.target.checked)}
                sx={{
                  color: "#3299FF",
                  "&.Mui-checked": { color: "#3299FF" },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                Add as work note (internal only)
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCommentDialogOpen(false)}
            sx={{
              color: "#666",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkCommentSubmit}
            variant="contained"
            disabled={!bulkComment.trim()}
            sx={{
              backgroundColor: "#3299FF",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              "&:hover": { backgroundColor: "#2080EF" },
            }}
          >
            Add Comment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Reassign Dialog */}
      <Dialog
        open={reassignDialogOpen}
        onClose={() => {
          setReassignDialogOpen(false);
          setSelectedUser(null);
          setAssignToSearch("");
          setUserOptions([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ fontFamily: "Urbanist, Verdana, Arial, sans-serif" }}
        >
          Bulk Reassign Tasks
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: "#666",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            }}
          >
            Reassign {selectedTasks.length} selected task
            {selectedTasks.length !== 1 ? "s" : ""} to a user
          </Typography>

          <Autocomplete
            fullWidth
            options={userOptions}
            value={selectedUser}
            onChange={(event, newValue) => setSelectedUser(newValue)}
            onInputChange={(event, newInputValue) => {
              setAssignToSearch(newInputValue);
              handleUserSearch(newInputValue);
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return `${option.name || option.user_name || "Unknown"} (${
                option.email || option.user_name || ""
              })`;
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    {option.name || option.user_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      color: "#666",
                    }}
                  >
                    {option.email || option.user_name}
                  </Typography>
                </Box>
              </Box>
            )}
            loading={userSearchLoading}
            loadingText="Searching users..."
            noOptionsText={
              assignToSearch.length < 2
                ? "Type at least 2 characters to search"
                : "No users found"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search User"
                placeholder="Search by name or email..."
                helperText="Type at least 2 characters to search ServiceNow users"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {userSearchLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  },
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReassignDialogOpen(false);
              setSelectedUser(null);
              setAssignToSearch("");
              setUserOptions([]);
            }}
            sx={{
              color: "#666",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkReassignSubmit}
            variant="contained"
            disabled={!selectedUser}
            sx={{
              backgroundColor: "#3299FF",
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              "&:hover": { backgroundColor: "#2080EF" },
            }}
          >
            Reassign Tasks
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkActionsBar;
